import styled from '@emotion/styled';
import { forwardRef } from 'react';
import { colors, fonts, radii, buttonSizes, breakpoints } from './tokens';

// Figma "Button / Buttons" (Size x Type x State).
//   variant: primary | secondary | destructive | success | tertiary | toggle
//   size:    sm (28) | md (40) | lg (56) | xl (64, session nav row; 52 on phones)
// Session names: End Session = destructive, Back = secondary, Next / Begin / Save Test Results = primary.
const VARIANTS = {
  primary: {
    bg: colors.primary,
    fg: colors.white,
    border: colors.primary,
    hoverBg: colors.primaryHover,
    hoverBorder: colors.primaryHover,
    activeBg: colors.primaryPressed,
  },
  secondary: {
    bg: colors.white,
    fg: colors.slate[700],
    border: colors.slate[200],
    hoverBg: colors.slate[50],
    hoverBorder: colors.slate[300],
    activeBg: colors.slate[100],
  },
  destructive: {
    bg: colors.error[500],
    fg: colors.white,
    border: colors.error[500],
    hoverBg: colors.error[600],
    hoverBorder: colors.error[600],
    activeBg: colors.error[700],
  },
  success: {
    bg: colors.success[500],
    fg: colors.white,
    border: colors.success[500],
    hoverBg: colors.success[600],
    hoverBorder: colors.success[600],
    activeBg: colors.success[700],
  },
  // Selected state of a two-option toggle ("Use Facial Recognition" / "Input Manually"): use primary.
  toggle: {
    bg: colors.white,
    fg: colors.slate[700],
    border: colors.slate[200],
    hoverBg: colors.slate[50],
    hoverBorder: colors.slate[300],
    activeBg: colors.slate[100],
  },
  // White outline on the teal header ("See Notes").
  header: {
    bg: 'transparent',
    fg: colors.white,
    border: 'rgba(255,255,255,0.85)',
    hoverBg: 'rgba(255,255,255,0.12)',
    hoverBorder: colors.white,
    activeBg: 'rgba(255,255,255,0.2)',
  },
};

const forward = (prop) => !['variant', 'size', 'block', 'iconLeft', 'iconRight', 'compact'].includes(prop);

const StyledButton = styled('button', { shouldForwardProp: forward })`
  --btn-h: ${({ size }) => buttonSizes[size].height}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  height: var(--btn-h);
  min-height: var(--btn-h);
  padding: 0 ${({ size }) => buttonSizes[size].padX}px;
  width: ${({ block }) => (block ? '100%' : 'auto')};
  border-radius: ${({ variant }) => (variant === 'tertiary' ? radii.buttonTertiary : radii.button)}px;
  font-family: ${fonts.family};
  font-weight: 700;
  font-size: ${({ size }) => buttonSizes[size].font}px;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease, box-shadow 120ms ease;

  ${({ variant }) => {
    if (variant === 'tertiary') {
      return `
        background: transparent;
        color: ${colors.accent};
        border: 0;
        padding-left: 0;
        padding-right: 0;
        height: auto;
        min-height: 0;
        &:hover:not(:disabled) { text-decoration: underline; }
      `;
    }
    const v = VARIANTS[variant] || VARIANTS.primary;
    return `
      background: ${v.bg};
      color: ${v.fg};
      border: 1px solid ${v.border};
      &:hover:not(:disabled) { background: ${v.hoverBg}; border-color: ${v.hoverBorder}; }
      &:active:not(:disabled) { background: ${v.activeBg}; }
    `;
  }}

  &:focus-visible {
    outline: 3px solid ${colors.brand[300]};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    ${({ variant }) =>
      variant === 'tertiary'
        ? `color: ${colors.slate[400]};`
        : `background: ${colors.slate[200]}; border-color: ${colors.slate[200]}; color: ${colors.slate[500]};`}
  }

  svg {
    flex-shrink: 0;
  }

  ${({ size }) =>
    size === 'xl'
      ? `@media (max-width: ${breakpoints.phone - 1}px) {
          --btn-h: 52px;
          font-size: 16px;
          padding: 0 18px;
        }`
      : ''}
`;

export const Button = forwardRef(({ variant = 'primary', size = 'md', iconLeft, iconRight, children, type = 'button', ...rest }, ref) => (
  <StyledButton ref={ref} type={type} variant={variant} size={size} {...rest}>
    {iconLeft}
    {children}
    {iconRight}
  </StyledButton>
));
Button.displayName = 'Button';

// Pre-named session buttons so every screen uses the same variant/size.
export const EndSessionButton = ({ children = 'End Session', ...p }) => <Button variant='destructive' size='xl' {...p}>{children}</Button>;
export const BackButton = ({ children = 'Back', ...p }) => <Button variant='secondary' size='xl' {...p}>{children}</Button>;
export const NextButton = ({ children = 'Next', ...p }) => <Button variant='primary' size='xl' {...p}>{children}</Button>;
export const SaveResultsButton = ({ children = 'Save Test Results', ...p }) => <Button variant='primary' size='xl' {...p}>{children}</Button>;

// Figma "Button Set": two buttons with an 8px gap (wraps on narrow screens).
export const ButtonSet = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ gap = 8 }) => gap}px;
  align-items: center;
  justify-content: ${({ justify = 'flex-start' }) => justify};
`;

// Two-option segmented toggle ("Use Facial Recognition" | "Input Manually", "On" | "Off" as buttons).
export const ToggleGroup = ({ options, value, onChange, size = 'md', ariaLabel }) => (
  <ButtonSet role='radiogroup' aria-label={ariaLabel} gap={16}>
    {options.map((o) => {
      const opt = typeof o === 'string' ? { value: o, label: o } : o;
      const selected = opt.value === value;
      return (
        <Button
          key={opt.value}
          role='radio'
          aria-checked={selected}
          variant={selected ? 'primary' : 'toggle'}
          size={size}
          onClick={() => onChange?.(opt.value)}
          disabled={opt.disabled}
        >
          {opt.label}
        </Button>
      );
    })}
  </ButtonSet>
);

export default Button;
